/** The fields grouping needs, so tests don't have to build a full `AssetDetails`. */
export interface GroupableServer {
	uniq_id: string;
	isNutanixPrivateCloud?: boolean;
	isNutanixVm?: boolean;
	region_id?: number;
}

export interface ServerListEntry<T extends GroupableServer> {
	server: T;
	/** The VMs that are loaded and mapped to the VPC, in the order they were given. Always empty for a server that is not a VPC. */
	children: T[];
}

/**
 * Groups Nutanix VMs under their VPC, keeping the order of `servers`. Only
 * Nutanix VPCs are parents; VMware vDCs and Private Parents stay flat, along
 * with their children.
 *
 * Each Nutanix VM is grouped under the loaded VPC with the same `region_id`;
 * an account has one VPC per region. A VM with no loaded VPC in its region
 * stays top-level, so a VM can sit flat until the page holding its VPC loads.
 * If more than one loaded VPC shares a region, the right parent is unknown, so
 * that region's VMs stay top-level rather than all landing under one VPC.
 */
const groupServersByParent = <T extends GroupableServer>(
	servers: T[],
): ServerListEntry<T>[] => {
	const serversById = new Map<string, T>();

	servers.forEach((server) => {
		if (!serversById.has(server.uniq_id)) {
			serversById.set(server.uniq_id, server);
		}
	});

	const vpcIdByRegionId = new Map<number, string>();
	const ambiguousRegionIds = new Set<number>();

	serversById.forEach(
		({ isNutanixPrivateCloud, region_id: regionId, uniq_id: uniqId }) => {
			if (!isNutanixPrivateCloud || regionId === undefined) return;

			if (vpcIdByRegionId.has(regionId)) {
				ambiguousRegionIds.add(regionId);

				return;
			}

			vpcIdByRegionId.set(regionId, uniqId);
		},
	);

	const childrenByParentId = new Map<string, T[]>();
	const entries: T[] = [];

	serversById.forEach((server) => {
		const vpcId =
			server.isNutanixVm &&
			server.region_id !== undefined &&
			!ambiguousRegionIds.has(server.region_id)
				? vpcIdByRegionId.get(server.region_id)
				: undefined;

		if (vpcId && vpcId !== server.uniq_id) {
			const children = childrenByParentId.get(vpcId) ?? [];

			children.push(server);
			childrenByParentId.set(vpcId, children);

			return;
		}

		entries.push(server);
	});

	return entries.map((server) => ({
		server,
		children: childrenByParentId.get(server.uniq_id) ?? [],
	}));
};

/**
 * The VPC `groupServersByParent` would nest `vm` under, or `undefined` when
 * there is none (no VPC in its region, or several). Goes through the grouping
 * itself so a VM has one parent whichever page asks.
 */
export const findParentVpc = <T extends GroupableServer>(
	vm: T,
	vpcs: T[],
): T | undefined =>
	groupServersByParent([...vpcs, vm]).find(({ children }) =>
		children.some(({ uniq_id: uniqId }) => uniqId === vm.uniq_id),
	)?.server;

export default groupServersByParent;
